import { useQueries } from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";
import { useHotel } from "@/hooks/useHotel";
import { feedbacksService } from "@/services/feedbacks-service";
import { roomService } from "@/services/roomService";
import { RoomStatus } from "@/types/room";
import { requestService } from "@/services/requestService";
import { RequestType } from "@/types/request";
import { hotelService } from "@/services/hotel-service";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

const FEEDBACK_SAMPLE_LIMIT = 500;
const REQUESTS_SAMPLE_LIMIT = 500;

export type DashboardKeyMetricsState = {
  occupancy: {
    value: number | "—";
    secondary: string;
    secondaryClassName: string;
  };
  guestSatisfaction: {
    value: string | "—";
    secondary: string;
    valueClassName?: string;
    secondaryClassName?: string;
  };
  upsells: {
    value: number | "—";
    secondary: string;
    secondaryClassName: string;
  };
};

function buildLoadedDashboardKeyMetrics(
  occupiedRooms: number,
  totalRooms: number,
  avgRating: number | null,
  totalRequestsToday: number,
  uniqueGuestsWithRequests: number,
  inHouseTotal: number,
): DashboardKeyMetricsState {
  const pct =
    totalRooms === 0
      ? 0
      : Math.min(100, Math.round((occupiedRooms / totalRooms) * 100));
  const upsellsPct =
    inHouseTotal === 0
      ? 0
      : Math.min(
          100,
          Math.round((uniqueGuestsWithRequests / inHouseTotal) * 100),
        );

  return {
    occupancy: {
      value: occupiedRooms,
      secondary: `${pct}%`,
      secondaryClassName: "text-eathy-comfort-2 font-bold",
    },
    guestSatisfaction: {
      value: avgRating === null ? "-" : avgRating.toFixed(1),
      secondary: "/5",
      ...(avgRating === null
        ? {
            secondaryClassName: "text-monochrome-core-2/75 font-semibold",
          }
        : {
            valueClassName: "text-royal-glow-4 font-bold",
            secondaryClassName: "text-monochrome-core-2/75 font-semibold",
          }),
    },
    upsells: {
      value: totalRequestsToday,
      secondary: `${upsellsPct}%`,
      secondaryClassName: "text-royal-glow-4 font-bold",
    },
  };
}

export const useDashboardKeyMetrics = (): DashboardKeyMetricsState => {
  const { hotelId } = useHotel();
  const { capabilities, hasFeedbacksAccess } = useAccessCapabilities();
  const enabled = Boolean(hotelId);
  const feedbacksEnabled = enabled && hasFeedbacksAccess;
  const requestsEnabled = enabled && capabilities.canUseRequestsSystem;

  const todayStart = startOfDay(new Date()).toISOString();
  const todayEnd = endOfDay(new Date()).toISOString();

  const queries = useQueries({
    queries: [
      {
        queryKey: ["dashboard", "keyMetrics", "roomsTotal", hotelId] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return roomService.getRooms(id, {}, 1, 1);
        },
        enabled,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "keyMetrics",
          "roomsOccupied",
          hotelId,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return roomService.getRooms(
            id,
            { status: RoomStatus.OCCUPIED },
            1,
            1,
          );
        },
        enabled,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "keyMetrics",
          "feedbackRequests",
          hotelId,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return feedbacksService.getAllRequestFeedbacks({
            hotelId: id,
            page: 1,
            limit: FEEDBACK_SAMPLE_LIMIT,
          });
        },
        enabled: feedbacksEnabled && capabilities.canUseRequestsSystem,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "keyMetrics",
          "feedbackCheckouts",
          hotelId,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return feedbacksService.getAllCheckoutFeedbacks({
            hotelId: id,
            page: 1,
            limit: FEEDBACK_SAMPLE_LIMIT,
          });
        },
        enabled: feedbacksEnabled,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "keyMetrics",
          "feedbackCommunications",
          hotelId,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return feedbacksService.getAllCommunicationFeedbacks({
            hotelId: id,
            page: 1,
            limit: FEEDBACK_SAMPLE_LIMIT,
          });
        },
        enabled: feedbacksEnabled,
        staleTime: 120_000,
      },
      {
        queryKey: ["dashboard", "keyMetrics", "inHouseTotal", hotelId] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return hotelService.getSettlement(
            { type: "In-House", perPage: 1 },
            id,
          );
        },
        enabled: requestsEnabled,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "keyMetrics",
          "upsellsToday",
          hotelId,
          todayStart,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return requestService.getRequests({
            type: RequestType.GUEST,
            hotelId: id,
            startDate: todayStart,
            endDate: todayEnd,
            limit: REQUESTS_SAMPLE_LIMIT,
          });
        },
        enabled: requestsEnabled,
        staleTime: 120_000,
      },
    ],
  });

  const isLoading = enabled && queries.some((q) => q.isLoading);
  const hasError = queries.some((q) => q.isError);

  const dash: DashboardKeyMetricsState = {
    occupancy: {
      value: "—",
      secondary: "",
      secondaryClassName: "text-eathy-comfort-2 font-bold",
    },
    guestSatisfaction: {
      value: "—",
      secondary: "",
    },
    upsells: {
      value: "—" as const,
      secondary: "",
      secondaryClassName: "text-royal-glow-4 font-bold",
    },
  };

  if (!enabled) {
    return dash;
  }

  if (isLoading || hasError) {
    return dash;
  }

  const totalRooms = queries[0].data?.total ?? 0;
  const occupiedRooms = queries[1].data?.total ?? 0;

  const feedbackRows = hasFeedbacksAccess
    ? [
        ...(capabilities.canUseRequestsSystem
          ? (queries[2].data?.data ?? [])
          : []),
        ...(queries[3].data?.data ?? []),
        ...(queries[4].data?.data ?? []),
      ]
    : [];
  const ratings = feedbackRows
    .map((f) => f.rating)
    .filter((r): r is number => typeof r === "number" && Number.isFinite(r));
  const avgRating =
    ratings.length === 0
      ? null
      : ratings.reduce((sum, r) => sum + r, 0) / ratings.length;

  const inHouseTotal = queries[5].data?.total ?? 0;
  const upsellRequests = capabilities.canUseRequestsSystem
    ? (queries[6].data?.data ?? [])
    : [];
  const totalRequestsToday = capabilities.canUseRequestsSystem
    ? (queries[6].data?.total ?? 0)
    : 0;
  const uniqueGuestsWithRequests = new Set(
    upsellRequests
      .map((r) => r.guestId)
      .filter((id): id is string => Boolean(id)),
  ).size;
  return buildLoadedDashboardKeyMetrics(
    occupiedRooms,
    totalRooms,
    avgRating,
    totalRequestsToday,
    uniqueGuestsWithRequests,
    inHouseTotal,
  );
};
